import type {
  FlattenedIssueTypeFilteringDtoOptions,
  FlattenedIssueTypeSortingDtoOptions,
} from '@issuemanager/api-client';
import { getSortingModelByField } from '@issuemanager/apps/client/components';
import { useDateTimeFormat } from '@issuemanager/apps/client/hooks';
import { getOptionalPlatformISOString } from '@issuemanager/apps/client/utils';
import {
  ISSUE_TYPES_FIELD_CREATED,
  ISSUE_TYPES_FIELD_CREATED_BY,
  ISSUE_TYPES_FIELD_ISSUE_SUBTYPE,
  ISSUE_TYPES_FIELD_ISSUE_TYPE,
  ISSUE_TYPES_FIELD_ISSUE_TYPE_MANAGER,
} from '@issuemanager/constants';
import type { GridSortModel } from '@mui/x-data-grid-pro';
import { IssueTypesGridFilterModel } from './types';

export function useFlattenedIssueTypesQueryParams(
  sortModel: GridSortModel,
  filterModel: IssueTypesGridFilterModel,
): {
  sort: FlattenedIssueTypeSortingDtoOptions;
  filter: FlattenedIssueTypeFilteringDtoOptions;
} {
  return {
    sort: useFlattenedIssueTypesQuerySortParam(sortModel),
    filter: useFlattenedIssueTypesQueryFilterParam(filterModel),
  };
}

function useFlattenedIssueTypesQueryFilterParam(
  filterModel: IssueTypesGridFilterModel,
): FlattenedIssueTypeFilteringDtoOptions {
  const { timezoneOffset } = useDateTimeFormat();

  const issueTypeFilter = filterModel[ISSUE_TYPES_FIELD_ISSUE_TYPE];
  const issueSubtypeFilter = filterModel[ISSUE_TYPES_FIELD_ISSUE_SUBTYPE];
  const createdAtFilter = filterModel[ISSUE_TYPES_FIELD_CREATED];
  const managerFilter = filterModel[ISSUE_TYPES_FIELD_ISSUE_TYPE_MANAGER];
  const createdByFilter = filterModel[ISSUE_TYPES_FIELD_CREATED_BY];

  const issueTypeId = issueTypeFilter?.map((issueType) => issueType.name);
  const issueSubtypeId = issueSubtypeFilter?.map(
    (issueSubtype) => issueSubtype.id,
  );
  const managers = managerFilter?.map((manager) => manager.id);
  const addedAtRangeStart = getOptionalPlatformISOString(
    createdAtFilter?.[0],
    timezoneOffset,
  );
  const addedAtRangeEnd = getOptionalPlatformISOString(
    createdAtFilter?.[1],
    timezoneOffset,
  );
  const addedBy = createdByFilter?.map((user) => user.id);

  return {
    issueTypeId,
    issueSubtypeId,
    addedAtRangeStart,
    addedAtRangeEnd,
    managers,
    addedBy,
  };
}

function useFlattenedIssueTypesQuerySortParam(
  sortModel: GridSortModel,
): FlattenedIssueTypeSortingDtoOptions {
  const issueType = getSortingModelByField(
    sortModel,
    ISSUE_TYPES_FIELD_ISSUE_TYPE,
  );
  const issueSubtype = getSortingModelByField(
    sortModel,
    ISSUE_TYPES_FIELD_ISSUE_SUBTYPE,
  );
  const addedAt = getSortingModelByField(sortModel, ISSUE_TYPES_FIELD_CREATED);
  const addedBy = getSortingModelByField(
    sortModel,
    ISSUE_TYPES_FIELD_CREATED_BY,
  );

  return {
    issueType,
    issueSubtype,
    addedAt,
    addedBy,
  };
}
